// Tripwire only: agents/users can override by unbinding.
//
// Binds a git checkout (main or linked worktree) to a single entity so push/pull
// refuse to run against a different one once bound. NOT a concurrency lock — a
// per-worktree routing guard against a prompt landing in the wrong checkout.
//
// One module, two descriptors: a worktree may legitimately be bound to one hub
// AND one base at the same time, so the two bindings are separate FILES with
// separate ids — but everything around that pair of values (the `resolveGitDir`
// cwd cache, the legacy-filename fallback, the ENOENT-vs-EACCES distinction, id
// validation, and the assert/auto-bind helpers) is subtree-agnostic and lives
// here exactly once. Two copies of the corrupt-file and EACCES semantics would
// be two things to keep in sync
// (docs/reference/rekor-platform-contracts.md §3.3).

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';

export interface BindingDescriptor {
  /** Filename under the per-worktree git dir, e.g. 'wayai-binding'. */
  filename: string;
  /**
   * Pre-rename filename still read as a fallback and swept on write/clear, so a
   * checkout bound before a rename keeps its binding across the upgrade.
   */
  legacyFilename?: string;
  /**
   * Structural validity of an id for this subtree. A hub id is a UUID; a base id
   * is a slug — so this is a per-descriptor value, not shared machinery.
   */
  isValidId: (id: string) => boolean;
  /** Noun for user-facing messages, e.g. 'hub'. */
  noun: string;
  /** What an invalid id is called in the write-validation error, e.g. 'hub_id'. */
  idLabel: string;
  /** Full command that clears this binding, e.g. 'wayai unbind'. */
  unbindCommand: string;
  /** Full command that rebinds this worktree, e.g. 'wayai use'. */
  useCommand: string;
}

export interface WorktreeBinding {
  getBindingPath(): string | null;
  readBinding(): string | null;
  writeBinding(id: string): void;
  clearBinding(): boolean;
  assertMatchesBinding(resolvedId: string, label?: string): void;
  assertNoBindingBlocksCreation(newName: string): void;
  autoBindIfUnbound(id: string): boolean;
}

// Cache keyed by cwd so tests that chdir between checkouts re-resolve. Shared by
// every descriptor — `git rev-parse` is the same answer for all of them.
let _gitDirCache: { cwd: string; gitDir: string | null } | undefined;

function resolveGitDir(): string | null {
  const cwd = process.cwd();
  if (_gitDirCache && _gitDirCache.cwd === cwd) return _gitDirCache.gitDir;

  let gitDir: string | null;
  try {
    // `--git-dir` resolves to the per-worktree git dir (`.git/worktrees/<name>`
    // for a linked worktree), which is what makes the binding worktree-local.
    const raw = execFileSync('git', ['rev-parse', '--git-dir'], {
      cwd,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
    gitDir = raw ? path.resolve(cwd, raw) : null;
  } catch {
    gitDir = null;
  }
  _gitDirCache = { cwd, gitDir };
  return gitDir;
}

/** Build the binding API for one subtree. */
export function createWorktreeBinding(descriptor: BindingDescriptor): WorktreeBinding {
  const {
    filename,
    legacyFilename,
    isValidId,
    noun,
    idLabel,
    unbindCommand,
    useCommand,
  } = descriptor;

  function getBindingPath(): string | null {
    const gitDir = resolveGitDir();
    return gitDir ? path.join(gitDir, filename) : null;
  }

  function getLegacyBindingPath(): string | null {
    if (!legacyFilename) return null;
    const gitDir = resolveGitDir();
    return gitDir ? path.join(gitDir, legacyFilename) : null;
  }

  // Surfaces non-ENOENT errors (EACCES) so unreadable bindings don't masquerade
  // as unbound. Prefers the new filename; falls back to the legacy one so a
  // checkout bound before the rename stays bound. A present-but-corrupt primary
  // file wins (returns null = unbound) and does NOT fall through to the legacy.
  function readBinding(): string | null {
    for (const bindingPath of [getBindingPath(), getLegacyBindingPath()]) {
      if (!bindingPath) continue;
      let raw: string;
      try {
        raw = fs.readFileSync(bindingPath, 'utf-8');
      } catch (err) {
        if ((err as NodeJS.ErrnoException).code === 'ENOENT') continue;
        throw err;
      }
      const trimmed = raw.trim();
      return isValidId(trimmed) ? trimmed : null;
    }
    return null;
  }

  // Truly best-effort: a leftover legacy file is harmless (readBinding prefers
  // the new filename), so failure to remove it must NOT fail a writeBinding that
  // already persisted the new binding. Swallows every error, not just ENOENT.
  function removeLegacyBinding(): void {
    const legacyPath = getLegacyBindingPath();
    if (!legacyPath) return;
    try {
      fs.unlinkSync(legacyPath);
    } catch {
      // ignore — migration cleanup is optional
    }
  }

  function writeBinding(id: string): void {
    if (!isValidId(id)) {
      throw new Error(`Invalid ${idLabel}: ${id}`);
    }
    const bindingPath = getBindingPath();
    if (!bindingPath) {
      throw new Error(`Not inside a git repository — cannot write ${noun} binding.`);
    }
    fs.writeFileSync(bindingPath, `${id}\n`, 'utf-8');
    // Migrate off the legacy filename so the two names can't drift (best-effort).
    removeLegacyBinding();
  }

  // Returns true if a binding existed and was removed, false if absent. Removes
  // both the new and legacy files. Surfaces non-ENOENT errors.
  function clearBinding(): boolean {
    let removed = false;
    for (const bindingPath of [getBindingPath(), getLegacyBindingPath()]) {
      if (!bindingPath) continue;
      try {
        fs.unlinkSync(bindingPath);
        removed = true;
      } catch (err) {
        if ((err as NodeJS.ErrnoException).code !== 'ENOENT') throw err;
      }
    }
    return removed;
  }

  function assertMatchesBinding(resolvedId: string, label?: string): void {
    const bound = readBinding();
    if (!bound) return;
    if (bound === resolvedId) return;

    const target = label ? `${label} (${resolvedId})` : resolvedId;
    // Deliberately prints and exits rather than throwing: this is an expected
    // routing guard, not a bug, so it must stay out of the Sentry pipeline.
    console.error(
      [
        `This worktree is bound to ${noun} ${bound}, but the command resolved to ${target}.`,
        '',
        'This usually means a prompt was routed to the wrong worktree.',
        'Confirm with the user before unbinding. To override:',
        `  ${unbindCommand}             # clear the binding for this worktree`,
        `  ${useCommand} ${resolvedId}  # rebind this worktree to the new ${noun}`,
      ].join('\n'),
    );
    process.exit(1);
  }

  // Fail-secure: readBinding errors propagate (matches assertMatchesBinding).
  function assertNoBindingBlocksCreation(newName: string): void {
    const bound = readBinding();
    if (!bound) return;

    console.error(
      [
        `This worktree is bound to ${noun} ${bound}, but you're about to create a new ${noun} "${newName}" here.`,
        '',
        'This usually means a prompt was routed to the wrong worktree.',
        'Confirm with the user before unbinding. To override:',
        `  ${unbindCommand}             # clear the binding for this worktree`,
      ].join('\n'),
    );
    process.exit(1);
  }

  // Best-effort: errors don't fail the parent operation (which already succeeded).
  function autoBindIfUnbound(id: string): boolean {
    try {
      if (readBinding()) return false;
      writeBinding(id);
      console.log(`Worktree bound to ${noun} ${id} (run \`${unbindCommand}\` to clear).`);
      return true;
    } catch (err) {
      console.warn(
        `  Warning: could not auto-bind worktree (${err instanceof Error ? err.message : String(err)})`,
      );
      return false;
    }
  }

  return {
    getBindingPath,
    readBinding,
    writeBinding,
    clearBinding,
    assertMatchesBinding,
    assertNoBindingBlocksCreation,
    autoBindIfUnbound,
  };
}
